<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useThemeColors } from 'dashboard/composables/useThemeColors';
import LineChart from 'shared/components/charts/LineChart.vue';
const CHART_FONT_FAMILY = 'Inter, sans-serif';
import liveReports from 'dashboard/api/liveReports';

const store = useStore();
const { getChartColors } = useThemeColors();
const loading = ref(true);
const chartData = ref([]);
const totalConversations = ref(0);
const currentDate = ref('');
const refreshInterval = ref(null);

// eslint-disable-next-line no-unused-vars
const currentAccount = computed(() => store.getters.getCurrentAccount);

const preparedChartData = computed(() => {
  if (!chartData.value.length) return {};

  const labels = chartData.value.map(item => item.label);
  const data = chartData.value.map(item => item.value);
  const themePalette = getChartColors().chartPalette || [];
  const lineColor = themePalette.length ? themePalette[0] : '#1C7CFC';

  return {
    labels,
    datasets: [
      {
        type: 'line',
        label: 'Conversas por Hora',
        data: data,
        borderColor: lineColor,
        backgroundColor: `${lineColor}20`,
        pointBackgroundColor: lineColor,
        pointBorderColor: lineColor,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
        tension: 0.2,
      },
    ],
  };
});

const chartOptions = computed(() => {
  const chartColors = getChartColors();

  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 0,
    },
    backgroundColor: 'transparent',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: chartColors.tooltipBg,
        titleColor: chartColors.textPrimary,
        bodyColor: chartColors.textSecondary,
        borderColor: chartColors.tooltipBorder,
        borderWidth: 1,
        callbacks: {
          label: context => {
            return `${context.parsed.y} conversas às ${context.label}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Horas do Dia',
          color: chartColors.textSecondary,
          font: {
            family: CHART_FONT_FAMILY,
            size: 12,
          },
        },
        ticks: {
          fontFamily: CHART_FONT_FAMILY,
          color: chartColors.textSecondary,
        },
        grid: {
          color: chartColors.gridColor,
          drawOnChartArea: true,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Número de Conversas',
          color: chartColors.textSecondary,
          font: {
            family: CHART_FONT_FAMILY,
            size: 12,
          },
        },
        ticks: {
          fontFamily: CHART_FONT_FAMILY,
          beginAtZero: true,
          stepSize: 1,
          color: chartColors.textSecondary,
        },
        grid: {
          color: chartColors.gridColor,
          drawOnChartArea: true,
        },
      },
    },
  };
});

// Methods
const fetchHourlyData = async () => {
  try {
    loading.value = true;
    const response = await liveReports.getConversationsByHour({
      timezone_offset: new Date().getTimezoneOffset() / -60,
    });

    chartData.value = response.data.chart_data;
    totalConversations.value = response.data.total_conversations;
    currentDate.value = response.data.current_date;
  } catch (error) {
    /* empty */
  } finally {
    loading.value = false;
  }
};

const formatDate = date => {
  if (!date) return '';

  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  return new Date(date).toLocaleDateString('pt-BR', options);
};

const startAutoRefresh = () => {
  refreshInterval.value = setInterval(() => {
    fetchHourlyData();
  }, 300000);
};

onMounted(async () => {
  await fetchHourlyData();
  startAutoRefresh();
});

onUnmounted(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
  }
});
</script>

<template>
  <div
    class="bg-n-background dark:bg-n-solid-2 rounded-lg shadow-sm border border-n-weak p-6"
  >
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ $t('REPORT.HOURLY_CONVERSATIONS_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ currentDate ? formatDate(currentDate) : '' }}
        </p>
      </div>

      <div class="text-right">
        <div class="text-2xl font-bold text-n-slate-12">
          {{ totalConversations }}
        </div>
        <div class="text-sm text-n-slate-11">
          {{ $t('REPORT.TOTAL_CONVERSATIONS_TODAY') }}
        </div>
      </div>
    </div>

    <!-- Chart -->
    <div class="h-80 bg-transparent">
      <div v-if="loading" class="flex items-center justify-center h-full">
        <div class="flex items-center space-x-2">
          <div
            class="animate-spin rounded-full h-6 w-6 border-b-2 border-n-slate-11"
          />
          <span class="text-n-slate-11">{{ $t('REPORT.LOADING_DATA') }}</span>
        </div>
      </div>

      <div
        v-else-if="!chartData.length"
        class="flex items-center justify-center h-full"
      >
        <span class="text-n-slate-11">{{
          $t('REPORT.NO_DATA_AVAILABLE')
        }}</span>
      </div>

      <div v-else class="h-full bg-transparent chart-container">
        <LineChart
          :collection="preparedChartData"
          :chart-options="chartOptions"
        />
      </div>
    </div>

    <!-- Footer info -->
    <div class="mt-4 pt-4 border-t border-n-weak">
      <div class="flex items-center justify-between text-sm text-n-slate-11">
        <span>{{ $t('REPORT.AUTO_REFRESH_NOTE') }}</span>
        <button
          class="px-3 py-1 text-sm bg-n-alpha-black2 hover:bg-n-alpha-black3 dark:bg-n-solid-3 dark:hover:bg-n-solid-4 text-n-slate-12 rounded-md transition-colors disabled:opacity-50"
          :disabled="loading"
          @click="fetchHourlyData"
        >
          {{
            loading ? $t('REPORT.UPDATING_BUTTON') : $t('REPORT.UPDATE_BUTTON')
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart-container {
  background: transparent !important;
}

.chart-container :deep(canvas) {
  background: transparent !important;
}

.chart-container :deep(.chartjs-render-monitor) {
  background: transparent !important;
}
</style>
